import { StyleSheet, View, ViewProps } from 'react-native'
import {
  Draft,
  FloorplanPosition,
  WorkspaceTypeElement,
  useWorkspaceTypeElements,
  FloorplanData,
  WorkspaceData,
  useWorkspaceType,
} from 'zens-store'
import { Editable, Color, Spacing, Text, EditableItem, EditableItemInfo, Button } from 'zens-storybook'
import { observer } from 'mobx-react'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { workspaceIsPlaced } from '@dms/helper'
import { useZoomParam } from 'zens-router'

import FloorplanWorkspaceForm from './workspaceform'
import FloorplanElements from './elements'
import FloorplanAsset from './asset'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  menu: {
    borderColor: Color.calmGrey,
    borderRightWidth: 1,
    width: 360,
  },
  content: {
    flex: 1,
  },
  editor: {
    flex: 1,
    padding: Spacing.S,
  },
  head: {
    paddingHorizontal: Spacing.M,
    justifyContent: 'center',
    height: 74,
    borderColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
  formHead: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  title: {
    marginLeft: Spacing.XS,
  },
  form: {
    padding: Spacing.M,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: Spacing.M,
    borderTopWidth: 1,
    borderColor: Color.calmGrey,
  },
  button: {
    marginRight: Spacing.S,
  },
  top: {
    height: 74,
  },
})

interface Props extends ViewProps {
  workspaceId: number
  data: FloorplanData
  onUpdate?: (data: FloorplanData) => void
  onSave?: (draft: Draft<WorkspaceData>) => Promise<void>
  onRemove?: (draft: Draft<WorkspaceData>) => void
  onCancel?: () => Promise<void>
  errors?: Record<string, string>
}

const FloorplanDetail = (props: Props) => {
  const { workspaceId, data, onUpdate, onSave, onCancel, onRemove, errors, style, ...otherProps } = props
  const { floorplan } = data
  const { background, scale } = floorplan || {}
  const [element, setElement] = useState<WorkspaceTypeElement>()
  const { t } = useTranslation()
  const elements = useWorkspaceTypeElements()
  const [zoom, setZoom] = useZoomParam()
  const draft = useRef(new Draft<FloorplanData>({ ...data })).current
  const workspaces = draft.data.workspaces ?? []
  const workspace = workspaces.find((item) => `${item.id}` === `${workspaceId}`)
  const workspaceDraft = useRef(
    new Draft<WorkspaceData>({
      ...workspace,
      id: workspaceId,
      floor_id: data.id,
    }),
  ).current
  const workspaceType = useWorkspaceType({ workspaceTypeId: workspace?.workspace_type_id || 0 })
  const workspaceTypeElements = elements.data?.filter((item) => item.workspaceTypeId === workspace?.workspace_type_id)
  useEffect(() => {
    if (isNaN(workspaceId) && !workspaceIsPlaced(workspace)) {
      draft.update('workspaces', [...workspaces, { id: workspaceId }])
    }
  }, [])
  useEffect(() => {
    if (!element) {
      if (workspace?.element_id) {
        const workspaceElement = workspaceTypeElements?.find((item) => item.id === workspace.element_id)
        if (workspaceElement) select(workspaceElement)
      }
      if (workspaceTypeElements?.length === 1) {
        select(workspaceTypeElements[0])
      }
    }
  }, [workspaceTypeElements?.map((item) => item?.id).join(',')])

  function lengthBetweenPositions(positions: FloorplanPosition[]) {
    return Math.sqrt((positions[0]?.x - positions[1]?.x) ** 2 + (positions[0]?.y - positions[1]?.y) ** 2)
  }
  const scaleRatio = scale ? lengthBetweenPositions([scale.start, scale.end]) / scale.distance : 1

  const placedWorkspaces = workspaces
    .filter((item) => (`${item.id}` === `${workspaceId}` && element?.data) || workspaceIsPlaced(item))
    .map((item) => (`${item.id}` === `${workspaceId}` ? { ...item, element_id: element?.id ?? item.element_id } : item))
  const items: EditableItem[] = placedWorkspaces.map((item) => ({
    key: `${item.id}`,
    placement: item.position,
    selected: `${item.id}` === `${workspaceId}`,
  }))

  function select(value?: WorkspaceTypeElement) {
    setElement(value)
    const newWorkspaces = [...workspaces]
    const index = newWorkspaces.findIndex((item) => `${item.id}` === `${workspaceId}`)
    newWorkspaces[index].element_id = value?.id
    newWorkspaces[index].workspace_type_id = value?.workspaceTypeId
    draft.update('workspaces', newWorkspaces)
    workspaceDraft.update('floorplan', { ...workspaceDraft.data.floorplan, element_id: value?.id })
    workspaceDraft.update('workspace_type_id', value?.workspaceTypeId)
  }

  const save = async (value: Draft<WorkspaceData>) => {
    const workspaceId = value.data.id
    await onSave?.(value)
    if (draft.hasChanges) {
      draft.update(
        'workspaces',
        workspaces.map((item) => (`${item.id}` === `${workspaceId}` ? value.data : item)),
      )
      await onUpdate?.(draft.data)
    }
  }

  async function cancel() {
    return onCancel?.()
  }

  const update = (value: EditableItemInfo[]) => {
    const newWorkspaces = [...workspaces]
    value.forEach((info) => {
      const { item } = info
      const index = newWorkspaces.findIndex((workspace) => `${workspace.id}` === item.key)
      newWorkspaces[index] = { ...newWorkspaces[index], position: item.placement }
    })
    draft.update('workspaces', newWorkspaces)
    const index = value.findIndex((item) => `${item.item.key}` === `${workspaceId}`)
    workspaceDraft.update('floorplan', { ...workspaceDraft.data.floorplan, position: value[index]?.item.placement })
  }

  function unplace(value: EditableItemInfo) {
    const newWorkspaces = [...draft.data.workspaces!]
    const workspaceIndex = newWorkspaces.findIndex((workspace) => `${workspace.id}` === value.item.key)
    if (workspaceIndex >= 0 && !isNaN(newWorkspaces[workspaceIndex].id)) {
      newWorkspaces[workspaceIndex] = {
        ...newWorkspaces[workspaceIndex],
        position: undefined,
      }
      onUpdate?.({ ...data, workspaces: newWorkspaces })
    }
    onCancel?.()
  }

  function renderItem(info: EditableItemInfo) {
    const { item } = info
    const workspace = placedWorkspaces.find((workspace) => `${workspace.id}` === item.key)
    const itemElement = elements.data?.find((item) => item.id === workspace?.element_id)
    return (
      <FloorplanAsset uri={itemElement?.data?.src} width={Math.round((itemElement?.data?.width || 0) * scaleRatio)} />
    )
  }

  function renderElements() {
    return (
      <>
        <View style={styles.head}>
          {!isNaN(workspaceId) ? (
            <>
              <Text level={5}>{workspace?.name}</Text>
              <Text level={7}>{workspaceType.data?.name}</Text>
            </>
          ) : (
            <Text level={5}>
              <Trans>Add workspace</Trans>
            </Text>
          )}
        </View>
        <FloorplanElements
          workspaceTypeId={!isNaN(workspace?.id || NaN) ? workspace?.workspace_type_id : undefined}
          elementId={element?.id}
          onSelect={select}
        />
        <View style={styles.footer}>
          <Button title={t('Cancel')} light style={styles.button} onPress={cancel} testID="FloorplanDetail.Cancel" />
        </View>
      </>
    )
  }

  function renderForm() {
    return (
      <>
        <View style={[styles.head, styles.formHead]}>
          <FloorplanAsset uri={element?.data?.src} height={20} width={35} />
          <Text level={5} style={styles.title}>
            {element?.data?.name}
          </Text>
        </View>
        <FloorplanWorkspaceForm
          draft={workspaceDraft}
          style={styles.form}
          onCancel={cancel}
          onSave={save}
          onRemove={onRemove}
          errors={errors}
        />
      </>
    )
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.menu}>{workspaceIsPlaced(workspace) ? renderForm() : renderElements()}</View>
      <View style={styles.content}>
        <View style={styles.top} />
        <Editable
          style={styles.editor}
          items={items}
          renderItem={renderItem}
          onChange={update}
          onRemove={unplace}
          background={<FloorplanAsset uri={background || ''} width={4000} />}
          snapTo={Math.round(10 * scaleRatio)}
          zoom={zoom}
          onZoom={setZoom}
        />
      </View>
    </View>
  )
}

export default observer(FloorplanDetail)
